import {
    useImperativeHandle,
    useLayoutEffect,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useMemo,
    useRef
} from "react";
import {
    type LayoutChangeEvent,
    useWindowDimensions,
    AccessibilityInfo,
    Animated,
    Platform,
    View
} from "react-native";
import type {
    IDateSelectorRef,
    CalendarDay
} from "./type";
import type IDateSelectorProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    useDateSelectorSwap
} from "./hook";
import {
    getDateSelectorViewDateForTarget,
    getDateSelectorClampedViewDate,
    DATE_SELECTOR_NAVIGATION_KEYS,
    getDateSelectorFirstDayOfWeek,
    getDateSelectorNavigationDate,
    getDateSelectorTabStopIndex,
    getDateSelectorMonthOptions,
    getDateSelectorVisibleWeeks,
    getDateSelectorRangeResult,
    getDateSelectorYearOptions,
    getDateSelectorMonthsWidth,
    getDateSelectorNavigation,
    getDateSelectorWeekNumber,
    getDateSelectorPageIndex,
    getDateSelectorWeekDays,
    getDateSelectorTitle,
    getDateSelectorDays,
    dateSelect
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast
} from "../../core/hooks";
import {
    type RefForwardingComponent,
    type NCoreUIKitIcon
} from "../../types";
import {
    useBreakpoint
} from "../../helpers/pointer";
import type {
    CalendarCellKeyboardEventType
} from "../calendarCell/type";
import {
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon
} from "lucide-react-native";
import moment from "moment";
import useCalendarCellHandlers from "../calendarCell/hook";
import AnimatedIcon from "../animatedIcon";
import CalendarCell from "../calendarCell";
import NativeSelect from "../nativeSelect";
import Button from "../button";
import Text from "../text";

const PreviousMonthIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const NextMonthIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronRightIcon}
    animation="signature"
    color={color}
    size={size}
/>;

type MonthView = {
    days: Array<CalendarDay>;
    viewDate: Date;
    offset: number;
};

const DateSelector: RefForwardingComponent<IDateSelectorRef, IDateSelectorProps> = ({
    multipleSelectMinimumRequiredDayCount,
    localeBasedFirstDayOfWeek = true,
    isShowTodayIndicator = true,
    dayOfWeekLength = "short",
    isShowWeekNumber = false,
    captionLayout = "label",
    multipleSelectDayLimit,
    setIsSheetContentReady,
    viewDate: viewDateProp,
    selectMultipleObject,
    isShowCard = true,
    customLocalize,
    monthCount = 1,
    selectObject,
    customTheme,
    dateRange,
    maxDate,
    minDate,
    variant,
    date
}, ref) => {
    const {
        controlHeights,
        shapeConfigs,
        radiuses,
        borders,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        width: windowWidth
    } = useWindowDimensions();

    const cellRefs = useRef<Array<View | null>>([]);

    const [
        viewDate,
        setViewDate
    ] = useState(() => viewDateProp ? viewDateProp : dateSelect({
        dateRange,
        variant,
        date
    }));

    const [
        activeDate,
        setActiveDate
    ] = useState<Date | undefined>(undefined);

    const [
        focusRequest,
        setFocusRequest
    ] = useState(0);

    const [
        measuredWidth,
        setMeasuredWidth
    ] = useState<number | undefined>(undefined);

    const [
        liveState,
        setLiveState
    ] = useState({
        text: getDateSelectorTitle({
            activeLocale,
            viewDate
        }),
        isMuted: false
    });

    const breakpoint = useBreakpoint();

    const isCardShown = isShowCard && breakpoint !== "compact";

    const isStretched = !isShowCard;

    const weekColumnWidth = isShowWeekNumber ? controlHeights.small : 0;

    const twoMonthsMinWidth = getDateSelectorMonthsWidth({
        isShowWeekNumber,
        controlHeights,
        monthCount: 2,
        isStretched,
        spaces
    }) + (isCardShown ? (spaces.spacingMd + borders.line) * 2 : 0);

    const visibleMonthCount = monthCount === 2 && windowWidth >= twoMonthsMinWidth + spaces.spacingMd * 2 && (measuredWidth === undefined || measuredWidth >= twoMonthsMinWidth) ? 2 : 1;

    const isMultiMonth = visibleMonthCount === 2;

    const firstDayOfWeek = getDateSelectorFirstDayOfWeek({
        localeBasedFirstDayOfWeek,
        activeLocale
    });

    const secondViewDate = useMemo(() => moment(viewDate).startOf("month").add(1, "months").toDate(), [
        viewDate
    ]);

    const days = useMemo(() => getDateSelectorDays({
        firstDayOfWeek,
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    }), [
        firstDayOfWeek,
        dateRange,
        viewDate,
        maxDate,
        minDate,
        variant,
        date
    ]);

    const secondDays = useMemo(() => isMultiMonth ? getDateSelectorDays({
        viewDate: secondViewDate,
        firstDayOfWeek,
        dateRange,
        maxDate,
        minDate,
        variant,
        date
    }) : [], [
        secondViewDate,
        firstDayOfWeek,
        isMultiMonth,
        dateRange,
        maxDate,
        minDate,
        variant,
        date
    ]);

    const allDays = useMemo(() => isMultiMonth ? days.concat(secondDays) : days, [
        isMultiMonth,
        secondDays,
        days
    ]);

    const monthViews: Array<MonthView> = isMultiMonth ? [
        {
            offset: 0,
            viewDate,
            days
        },
        {
            viewDate: secondViewDate,
            offset: days.length,
            days: secondDays
        }
    ] : [
        {
            offset: 0,
            viewDate,
            days
        }
    ];

    const weekDays = useMemo(() => getDateSelectorWeekDays({
        dayOfWeekLength,
        activeLocale,
        days
    }), [
        dayOfWeekLength,
        activeLocale,
        days
    ]);

    const isWeb = Platform.OS === "web";

    const tabStopIndex = getDateSelectorTabStopIndex({
        days: allDays,
        activeDate
    });

    const title = getDateSelectorTitle({
        activeLocale,
        viewDate
    });

    const announcedTitleRef = useRef(title);

    const isSelectChangeRef = useRef(false);

    const currentNavigation = getDateSelectorNavigation({
        viewDate: isMultiMonth ? secondViewDate : viewDate,
        maxDate,
        minDate
    });

    const isPreviousDisabled = getDateSelectorNavigation({
        viewDate,
        maxDate,
        minDate
    }).isPreviousDisabled;

    const {
        weekNumberHeaderContainer: weekNumberHeaderContainerDynamicStyle,
        nextPrevToolContainer: nextPrevToolContainerDynamicStyle,
        weekNumberContainer: weekNumberContainerDynamicStyle,
        captionContainer: captionContainerDynamicStyle,
        contentContainer: contentContainerDynamicStyle,
        weekDayContainer: weekDayContainerDynamicStyle,
        monthsContainer: monthsContainerDynamicStyle,
        monthContainer: monthContainerDynamicStyle,
        weeksContainer: weeksContainerDynamicStyle,
        headerSpacer: headerSpacerDynamicStyle,
        container: containerDynamicStyle,
        blankDay: blankDayDynamicStyle
    } = useStyles({
        monthCount: visibleMonthCount,
        weekColumnWidth,
        controlHeights,
        shapeConfigs,
        isCardShown,
        isStretched,
        radiuses,
        borders,
        spaces,
        colors
    });

    const swapStyle = useDateSelectorSwap({
        pageIndex: getDateSelectorPageIndex({
            viewDate
        }),
        customTheme
    });

    useLayoutEffect(() => {
        setIsSheetContentReady?.(true);
    }, [
        days
    ]);

    useEffect(() => {
        setLiveState({
            isMuted: isSelectChangeRef.current,
            text: title
        });

        isSelectChangeRef.current = false;
    }, [
        title
    ]);

    useEffect(() => {
        if(liveState.isMuted) {
            setLiveState((currentState) => ({
                ...currentState,
                isMuted: false
            }));
        }
    }, [
        liveState.isMuted
    ]);

    useEffect(() => {
        if(viewDateProp) {
            setViewDate(viewDateProp);
        }
    }, [
        viewDateProp
    ]);

    useLayoutEffect(() => {
        if(!focusRequest || !activeDate) {
            return;
        }

        const activeIndex = allDays.findIndex((dayItem) => dayItem.isCurrentMonth && dayItem.date.isSame(activeDate, "day"));

        cellRefs.current[activeIndex]?.focus();
    }, [
        focusRequest
    ]);

    useEffect(() => {
        if(announcedTitleRef.current === title) {
            return;
        }

        announcedTitleRef.current = title;

        if(Platform.OS !== "ios") {
            return;
        }

        AccessibilityInfo.announceForAccessibility(title);
    }, [
        title
    ]);

    useImperativeHandle(
        ref,
        () => ({
            changeCurrentMonth: (date: Date) => {
                setViewDate(date);
            }
        }),
        []
    );

    const onPreviousMonthPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).subtract(1, "months").toDate());
    };

    const onNextMonthPress = () => {
        setViewDate((currentViewDate) => moment(currentViewDate).add(1, "months").toDate());
    };

    const handleMeasureLayout = (event: LayoutChangeEvent) => {
        setMeasuredWidth(event.nativeEvent.layout.width);
    };

    const handleMonthSelect = (value: string) => {
        const nextViewDate = getDateSelectorClampedViewDate({
            viewDate: moment(viewDate).startOf("month").month(Number(value)).toDate(),
            monthCount: visibleMonthCount,
            maxDate,
            minDate
        });

        isSelectChangeRef.current = !moment(nextViewDate).isSame(viewDate, "month");

        setViewDate(nextViewDate);
    };

    const handleYearSelect = (value: string) => {
        const nextViewDate = getDateSelectorClampedViewDate({
            viewDate: moment(viewDate).startOf("month").year(Number(value)).toDate(),
            monthCount: visibleMonthCount,
            maxDate,
            minDate
        });

        isSelectChangeRef.current = !moment(nextViewDate).isSame(viewDate, "month");

        setViewDate(nextViewDate);
    };

    const onDayPress = (dayItem: CalendarDay) => {
        if(variant === "single") {
            selectObject(dayItem.date.toDate(), true);
            return;
        }

        if(variant !== "range") {
            return;
        }

        const rangeResult = getDateSelectorRangeResult({
            multipleSelectMinimumRequiredDayCount,
            multipleSelectDayLimit,
            day: dayItem.date,
            dateRange
        });

        if(rangeResult.errorKey) {
            NCoreUIKitToast.open({
                title: localize(rangeResult.errorKey)
            });
            return;
        }

        selectMultipleObject(rangeResult.dateRange);
    };

    const handleActiveDateChange = (nextDate: Date) => {
        setActiveDate((currentDate) => currentDate && moment(currentDate).isSame(nextDate, "day") ? currentDate : nextDate);
    };

    const onDayKeyDown = (event: CalendarCellKeyboardEventType, dayItem: CalendarDay) => {
        if(event.altKey || event.ctrlKey || event.metaKey || !DATE_SELECTOR_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        event.preventDefault();

        const targetDay = getDateSelectorNavigationDate({
            shiftKey: !!event.shiftKey,
            day: dayItem.date,
            firstDayOfWeek,
            key: event.key,
            direction,
            maxDate,
            minDate
        });

        if(!targetDay) {
            return;
        }

        setFocusRequest((currentRequest) => currentRequest + 1);

        handleActiveDateChange(targetDay.toDate());

        if(isMultiMonth) {
            const nextViewDate = getDateSelectorViewDateForTarget({
                monthCount: visibleMonthCount,
                target: targetDay,
                viewDate
            });

            if(nextViewDate) {
                setViewDate(nextViewDate);
            }

            return;
        }

        if(!targetDay.isSame(viewDate, "month")) {
            setViewDate(targetDay.toDate());
        }
    };

    const getDayTabIndex = (dayIndex: number): 0 | -1 => {
        return dayIndex === tabStopIndex ? 0 : -1;
    };

    const getCellHandlers = useCalendarCellHandlers({
        onFocus: (dayIndex) => {
            const dayItem = allDays[dayIndex];

            if(dayItem) {
                handleActiveDateChange(dayItem.date.toDate());
            }
        },
        onKeyDown: (event, dayIndex) => {
            const dayItem = allDays[dayIndex];

            if(dayItem) {
                onDayKeyDown(event, dayItem);
            }
        },
        onPress: (dayIndex) => {
            const dayItem = allDays[dayIndex];

            if(dayItem) {
                onDayPress(dayItem);
            }
        },
        cellRefs
    });

    const renderDay = (dayItem: CalendarDay, dayIndex: number) => {
        if(isMultiMonth && !dayItem.isCurrentMonth) {
            return <View
                style={[
                    stylesheet.weekNumberContainer,
                    blankDayDynamicStyle
                ]}
                key={`day-${dayIndex}`}
                aria-hidden={true}
            />;
        }

        const isKeyboardNavigable = isWeb && dayItem.isCurrentMonth;

        const cellHandlers = getCellHandlers(dayIndex);

        return <CalendarCell
            tabIndex={isKeyboardNavigable ? getDayTabIndex(dayIndex) : undefined}
            onKeyDown={isKeyboardNavigable ? cellHandlers.onKeyDown : undefined}
            onPress={dayItem.isCurrentMonth ? cellHandlers.onPress : undefined}
            onFocus={isKeyboardNavigable ? cellHandlers.onFocus : undefined}
            spreadBehaviour={isShowCard ? "baseline" : "stretch"}
            isToday={isShowTodayIndicator && dayItem.isToday}
            rangePosition={dayItem.rangePosition}
            isOutside={!dayItem.isCurrentMonth}
            isDisabled={dayItem.isDisabled}
            isSelected={dayItem.isSelected}
            customLocalize={customLocalize}
            date={dayItem.date.toDate()}
            customTheme={customTheme}
            key={`day-${dayIndex}`}
            ref={cellHandlers.ref}
        />;
    };

    const renderCaptionSelects = (monthDate: Date) => {
        return <View
            style={[
                stylesheet.captionContainer,
                captionContainerDynamicStyle
            ]}
        >
            <NativeSelect
                accessibilityLabel={localize("calendar-month-select")}
                value={String(moment(monthDate).month())}
                data={getDateSelectorMonthOptions({
                    viewDate: monthDate,
                    activeLocale,
                    maxDate,
                    minDate
                })}
                customLocalize={customLocalize}
                onChange={handleMonthSelect}
                isMinWidthEnabled={false}
                customTheme={customTheme}
                spreadBehaviour="free"
                placeholder=""
                size="small"
            />
            <NativeSelect
                accessibilityLabel={localize("calendar-year-select")}
                value={String(moment(monthDate).year())}
                data={getDateSelectorYearOptions({
                    viewDate: monthDate,
                    activeLocale,
                    maxDate,
                    minDate
                })}
                customLocalize={customLocalize}
                onChange={handleYearSelect}
                isMinWidthEnabled={false}
                customTheme={customTheme}
                spreadBehaviour="free"
                placeholder=""
                size="small"
            />
            <Text
                aria-live={liveState.isMuted ? "off" : "polite"}
                style={stylesheet.liveTitle}
                customTheme={customTheme}
                role="status"
            >
                {liveState.text}
            </Text>
        </View>;
    };

    const renderTitle = (monthTitle: string) => {
        return <Text
            customTheme={customTheme}
            variant="titleMediumSize"
            style={stylesheet.title}
            aria-live="polite"
            numberOfLines={1}
            color="high"
        >
            {monthTitle}
        </Text>;
    };

    const renderHeaderSpacer = () => {
        return <View
            style={headerSpacerDynamicStyle}
            aria-hidden={true}
        />;
    };

    const renderNextPrevTool = (monthView: MonthView, monthIndex: number) => {
        const isFirst = monthIndex === 0;

        const isLast = monthIndex === monthViews.length - 1;

        const monthTitle = getDateSelectorTitle({
            viewDate: monthView.viewDate,
            activeLocale
        });

        return <View
            style={[
                stylesheet.nextPrevToolContainer,
                nextPrevToolContainerDynamicStyle
            ]}
        >
            {
                isFirst ?
                    <Button
                        accessibilityLabel={localize("previous-month")}
                        isDisabled={isPreviousDisabled}
                        onPress={onPreviousMonthPress}
                        customTheme={customTheme}
                        icon={PreviousMonthIcon}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                        size="medium"
                    />
                :
                    renderHeaderSpacer()
            }
            {captionLayout === "dropdown" && isFirst ? renderCaptionSelects(monthView.viewDate) : renderTitle(monthTitle)}
            {
                isLast ?
                    <Button
                        isDisabled={currentNavigation.isNextDisabled}
                        accessibilityLabel={localize("next-month")}
                        onPress={onNextMonthPress}
                        customTheme={customTheme}
                        spreadBehaviour="free"
                        icon={NextMonthIcon}
                        variant="ghost"
                        type="neutral"
                        size="medium"
                    />
                :
                    renderHeaderSpacer()
            }
        </View>;
    };

    const renderWeekNumberHeader = () => {
        if(!isShowWeekNumber) {
            return null;
        }

        return <View
            style={[
                stylesheet.weekNumberContainer,
                weekNumberHeaderContainerDynamicStyle
            ]}
            aria-hidden={true}
        >
            <Text
                style={stylesheet.weekDayTitle}
                customTheme={customTheme}
                variant="labelSmallSize"
                numberOfLines={1}
                color="low"
            >
                {localize("calendar-week-short")}
            </Text>
        </View>;
    };

    const renderWeekDays = () => {
        return <View
            style={[
                stylesheet.weekContainer
            ]}
        >
            {renderWeekNumberHeader()}
            {weekDays.map((weekDay, weekDayIndex) => {
                return <View
                    key={`week-day-${weekDayIndex}`}
                    style={[
                        stylesheet.weekDayContainer,
                        weekDayContainerDynamicStyle
                    ]}
                >
                    <Text
                        accessibilityLabel={Platform.OS === "web" ? undefined : weekDay.longName}
                        style={stylesheet.weekDayTitle}
                        customTheme={customTheme}
                        variant="labelSmallSize"
                        numberOfLines={1}
                        color="low"
                    >
                        {weekDay.shortName}
                    </Text>
                </View>;
            })}
        </View>;
    };

    const renderWeekNumber = (weekDaysItem: Array<CalendarDay>) => {
        if(!isShowWeekNumber) {
            return null;
        }

        const weekNumber = getDateSelectorWeekNumber({
            days: weekDaysItem,
            activeLocale
        });

        if(weekNumber === undefined) {
            return null;
        }

        return <View
            aria-label={localize("calendar-week-number", [
                weekNumber
            ])}
            style={[
                stylesheet.weekNumberContainer,
                weekNumberContainerDynamicStyle
            ]}
            accessible={true}
            role="img"
        >
            <Text
                style={stylesheet.weekNumberTitle}
                customTheme={customTheme}
                variant="labelSmallSize"
                numberOfLines={1}
                color="low"
            >
                {weekNumber}
            </Text>
        </View>;
    };

    const renderWeeks = (monthView: MonthView) => {
        return <Animated.View
            style={[
                stylesheet.weeksContainer,
                weeksContainerDynamicStyle,
                swapStyle
            ]}
        >
            {getDateSelectorVisibleWeeks(monthView.days, isMultiMonth).map((weekDaysItem, weekIndex) => {
                return <View
                    style={[
                        stylesheet.weekContainer
                    ]}
                    key={`week-${weekIndex}`}
                >
                    {renderWeekNumber(weekDaysItem)}
                    {weekDaysItem.map((dayItem, dayIndex) => renderDay(dayItem, monthView.offset + weekIndex * weekDaysItem.length + dayIndex))}
                </View>;
            })}
        </Animated.View>;
    };

    const renderMonthContent = (monthView: MonthView) => {
        return <View
            aria-label={getDateSelectorTitle({
                viewDate: monthView.viewDate,
                activeLocale
            })}
            style={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle
            ]}
            role="group"
        >
            {renderWeekDays()}
            {renderWeeks(monthView)}
        </View>;
    };

    const renderCalendar = () => {
        if(!isMultiMonth) {
            return <View
                style={[
                    stylesheet.container,
                    containerDynamicStyle
                ]}
            >
                {monthViews.map((monthView, monthIndex) => <Fragment
                    key={`month-${monthIndex}`}
                >
                    {renderNextPrevTool(monthView, monthIndex)}
                    {renderMonthContent(monthView)}
                </Fragment>)}
            </View>;
        }

        return <View
            style={[
                stylesheet.container,
                containerDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.monthsContainer,
                    monthsContainerDynamicStyle
                ]}
            >
                {monthViews.map((monthView, monthIndex) => <View
                    style={[
                        stylesheet.monthContainer,
                        monthContainerDynamicStyle
                    ]}
                    key={`month-${monthIndex}`}
                >
                    {renderNextPrevTool(monthView, monthIndex)}
                    {renderMonthContent(monthView)}
                </View>)}
            </View>
        </View>;
    };

    if(monthCount !== 2) {
        return renderCalendar();
    }

    return <View
        style={stylesheet.measureContainer}
        onLayout={handleMeasureLayout}
    >
        <View
            style={[
                stylesheet.measureProbe,
                {
                    width: twoMonthsMinWidth
                }
            ]}
        />
        {renderCalendar()}
    </View>;
};
export default forwardRef(DateSelector);
